/**
 * NIZAM ONE ERP — Permission Service
 *
 * Consumes DB-driven RBAC via get_my_roles() and get_my_permissions_scoped() RPCs.
 * Caches per user+company with a 5-minute TTL.
 * Fails closed on errors (returns false for any permission check failure).
 *
 * SECURITY NOTE: This service provides UI-level permission checks only.
 * Database RLS policies are the authoritative security boundary.
 * Never rely solely on this service to protect sensitive data.
 */

import { supabase } from '../supabase';
import type { EffectivePermissions, EffectiveRole } from './types';
import { ALL_PERMISSION_KEYS } from './permissionKeys';
import { findNavItemForPath, type NavItem } from '../../config/navigation';

// ── Cache ─────────────────────────────────────────────────────────────────────
const CACHE_TTL_MS = 5 * 60 * 1000;  // 5 minutes
const permissionCache = new Map<string, EffectivePermissions>();

const cacheKey = (userId: string, companyId: string) => `${userId}:${companyId}`;

// ── Diagnostics (dev-mode only) ───────────────────────────────────────────────
function warnDev(message: string) {
  if (import.meta.env.DEV) {
    console.warn('[PermissionService]', message);
  }
}

// ── Core: load permissions from DB ────────────────────────────────────────────
export async function loadMyPermissions(
  companyId: string,
  userId?: string,
): Promise<EffectivePermissions> {
  if (!companyId) {
    warnDev('loadMyPermissions called without companyId — returning empty permissions');
    return emptyPermissions('', companyId);
  }

  // Get current user if not provided
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return emptyPermissions('', companyId);
    userId = user.id;
  }

  const key = cacheKey(userId, companyId);

  // Check cache first
  const cached = permissionCache.get(key);
  if (cached && Date.now() - cached.loadedAt < CACHE_TTL_MS) {
    return cached;
  }

  try {
    // Load roles and permissions in parallel
    const [rolesResult, permsResult] = await Promise.all([
      supabase.rpc('get_my_roles', { p_company_id: companyId }),
      supabase.rpc('get_my_permissions_scoped', { p_company_id: companyId }),
    ]);

    // supabase.rpc() resolves (never throws) on DB errors. Treat an error as a
    // failed load: fail closed, but surface it and don't cache it for 5 min.
    if (rolesResult.error || permsResult.error) {
      const e = rolesResult.error ?? permsResult.error;
      console.error(
        `[PermissionService] ${rolesResult.error ? 'get_my_roles' : 'get_my_permissions_scoped'} failed for company ${companyId}:`,
        e,
      );
      return emptyPermissions(userId, companyId);
    }

    const roles: EffectiveRole[] = (rolesResult.data ?? []) as EffectiveRole[];
    const permissionKeys: string[] = (permsResult.data ?? []).map(
      (p: { permission_key: string }) => p.permission_key
    );

    const isSuperAdmin = roles.some(r => r.role_key === 'super_admin');
    if (roles.length === 0) {
      warnDev(`get_my_roles returned no roles for company ${companyId} — every permission check will be false`);
    }

    // In dev mode, warn about unknown permission keys
    if (import.meta.env.DEV) {
      permissionKeys.forEach(k => {
        if (!ALL_PERMISSION_KEYS.has(k)) {
          warnDev(`Unknown permission key from DB: "${k}" — add to permissionKeys.ts`);
        }
      });
    }

    const effective: EffectivePermissions = {
      companyId,
      userId,
      roles,
      permissions: new Set(permissionKeys),
      permissionsArray: permissionKeys,
      loadedAt: Date.now(),
      isSuperAdmin,
    };

    permissionCache.set(key, effective);
    return effective;
  } catch (err) {
    warnDev(`Failed to load permissions for company ${companyId}: ${err}`);
    // Fail closed — return empty permissions on error
    return emptyPermissions(userId, companyId);
  }
}

// ── Cache management ──────────────────────────────────────────────────────────
export function getCachedPermissions(
  companyId: string,
  userId?: string,
): EffectivePermissions | null {
  if (!userId || !companyId) return null;
  const cached = permissionCache.get(cacheKey(userId, companyId));
  if (!cached || Date.now() - cached.loadedAt >= CACHE_TTL_MS) return null;
  return cached;
}

export function clearPermissionCache(companyId?: string, userId?: string): void {
  if (companyId && userId) {
    permissionCache.delete(cacheKey(userId, companyId));
  } else if (companyId) {
    // Clear all entries for this company
    for (const key of permissionCache.keys()) {
      if (key.endsWith(`:${companyId}`)) permissionCache.delete(key);
    }
  } else {
    permissionCache.clear();
  }
}

// ── Permission checks ─────────────────────────────────────────────────────────
export async function hasPermission(
  permissionKey: string,
  companyId: string,
): Promise<boolean> {
  if (!permissionKey || !companyId) return false;

  if (import.meta.env.DEV && !ALL_PERMISSION_KEYS.has(permissionKey)) {
    warnDev(`hasPermission called with unregistered key: "${permissionKey}"`);
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const effective = await loadMyPermissions(companyId, user.id);
  if (effective.isSuperAdmin) return true;
  return effective.permissions.has(permissionKey);
}

export async function hasAnyPermission(
  permissionKeys: string[],
  companyId: string,
): Promise<boolean> {
  if (!permissionKeys.length || !companyId) return false;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const effective = await loadMyPermissions(companyId, user.id);
  if (effective.isSuperAdmin) return true;
  return permissionKeys.some(k => effective.permissions.has(k));
}

export async function hasAllPermissions(
  permissionKeys: string[],
  companyId: string,
): Promise<boolean> {
  if (!permissionKeys.length || !companyId) return false;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const effective = await loadMyPermissions(companyId, user.id);
  if (effective.isSuperAdmin) return true;
  return permissionKeys.every(k => effective.permissions.has(k));
}

export async function hasRole(roleKey: string, companyId: string): Promise<boolean> {
  if (!roleKey || !companyId) return false;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const effective = await loadMyPermissions(companyId, user.id);
  return effective.roles.some(r => r.role_key === roleKey);
}

export async function hasAnyRole(roleKeys: string[], companyId: string): Promise<boolean> {
  if (!roleKeys.length || !companyId) return false;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const effective = await loadMyPermissions(companyId, user.id);
  return effective.roles.some(r => roleKeys.includes(r.role_key));
}

export async function canAccessModule(moduleKey: string, companyId: string): Promise<boolean> {
  // Module access = user has at least one permission in that module
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || !companyId) return false;
  const effective = await loadMyPermissions(companyId, user.id);
  if (effective.isSuperAdmin) return true;
  for (const pKey of effective.permissions) {
    if (pKey.startsWith(moduleKey + '.') || pKey.startsWith(moduleKey + '_')) return true;
  }
  return false;
}

export async function canAccessRoute(routeKey: string, companyId: string): Promise<boolean> {
  // Route access check — maps route segments to module keys
  // This is a simple heuristic; complex routes handled by route guards directly
  const routeModuleMap: Record<string, string> = {
    finance:        'finance',
    hr:             'hr',
    sales:          'sales',
    procurement:    'procurement',
    warehouse:      'warehouse',
    manufacturing:  'manufacturing',
    projects:       'projects',
    quality:        'quality',
    logistics:      'logistics',
    compliance:     'compliance',
    hse:            'hse',
    admin:          'system',
    settings:       'system',
    analytics:      'executive',
    reports:        'finance',
  };
  const topSegment = routeKey.split('/').find(s => s.length > 0) ?? '';
  const moduleKey  = routeModuleMap[topSegment] ?? topSegment;
  return canAccessModule(moduleKey, companyId);
}

export async function canPerformAction(
  actionKey: string,
  resourceKey: string,
  companyId: string,
): Promise<boolean> {
  const permissionKey = `${resourceKey}.${actionKey}`;
  return hasPermission(permissionKey, companyId);
}

// ── Sync helpers (when effective permissions are already loaded) ───────────────
/** Fast synchronous check when EffectivePermissions is already loaded */
export function checkPermission(
  effective: EffectivePermissions | null,
  permissionKey: string,
): boolean {
  if (!effective) return false;
  if (effective.isSuperAdmin) return true;
  return effective.permissions.has(permissionKey);
}

export function checkAnyPermission(
  effective: EffectivePermissions | null,
  permissionKeys: string[],
): boolean {
  if (!effective) return false;
  if (effective.isSuperAdmin) return true;
  return permissionKeys.some(k => effective.permissions.has(k));
}

// ── Cross-company checks (independent of activeCompanyId) ──────────────────────
//
// usePermissions()/checkPermission() above are scoped to ONE company
// (activeCompanyId) and return false for everything whenever that's null —
// which is the normal state for a user in Consolidated View, not an edge
// case. A UI decision like "can this notification's link even be opened"
// must not go blank just because the viewer isn't looking at a single
// company right now, so this checks across every company the user belongs
// to instead, mirroring the OR-across-companies shape the real DB
// authorization already uses (see approve_leave_request()'s
// `exists (select 1 from get_user_company_ids() c where user_has_permission(...))`).

/** Does the user hold this permission in ANY of their companies? */
export async function hasPermissionInAnyCompany(
  authUserId: string,
  companyIds: string[],
  permissionKey: string,
): Promise<boolean> {
  if (!authUserId || companyIds.length === 0) return false;
  try {
    const results = await Promise.all(
      companyIds.map(companyId =>
        supabase.rpc('user_has_permission', {
          p_user_id: authUserId,
          p_company_id: companyId,
          p_permission_key: permissionKey,
        }),
      ),
    );
    return results.some(r => r.data === true);
  } catch {
    return false;
  }
}

/**
 * Can the user open this app path, checked across all their companies rather
 * than only the currently active one? Looks the path up against the same
 * per-route permission requirements navigation.ts uses for sidebar
 * visibility (see findNavItemForPath) — a path with no matching nav item is
 * treated as accessible (fail-open: there's no data to check it against).
 */
export async function canOpenPathInAnyCompany(
  path: string,
  authUserId: string,
  companyIds: string[],
): Promise<boolean> {
  const item: NavItem | undefined = findNavItemForPath(path);
  if (!item) return true;
  if (!item.permission && !item.requiredPermissions) return true;

  const keys = item.requiredPermissions?.length ? item.requiredPermissions : [item.permission!];
  const results = await Promise.all(keys.map(k => hasPermissionInAnyCompany(authUserId, companyIds, k)));
  return item.permissionMode === 'all' ? results.every(Boolean) : results.some(Boolean);
}

// ── Internals ─────────────────────────────────────────────────────────────────
function emptyPermissions(userId: string, companyId: string): EffectivePermissions {
  return {
    companyId, userId, roles: [],
    permissions: new Set(),
    permissionsArray: [],
    loadedAt: Date.now(),
    isSuperAdmin: false,
  };
}
